import { useState, useCallback, useEffect, useRef, useMemo } from "react"
import { isToday, isYesterday, format, startOfDay } from "date-fns"

import { searchLog } from "@/lib/logger"
import { parseLabelEntry } from "@craft-agent/shared/labels"
import { fuzzyFilter } from "@craft-agent/shared/search"
import { getSessionTitle, getSessionStatus } from "@/utils/session"
import type { SessionMeta } from "@/atoms/sessions"
import type { ViewConfig } from "@craft-agent/shared/views"
import type { SessionFilter } from "@/contexts/NavigationContext"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INITIAL_DISPLAY_LIMIT = 50
const BATCH_SIZE = 50
const MAX_SEARCH_RESULTS = 100
const CONTENT_SEARCH_DEBOUNCE_MS = 300
const EMPTY_CONTENT_RESULTS = new Map<string, ContentSearchResult>()

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Filter mode for tri-state filtering: include shows only matching, exclude hides matching */
export type FilterMode = 'include' | 'exclude'

export interface DateGroup {
  date: Date
  label: string
  sessions: SessionMeta[]
}

export interface ContentSearchResult {
  matchCount: number
  snippet: string
}

/** Metadata for a collapsed group — emitted by the data pipeline so the renderer can show header-only groups */
export interface CollapsedGroupMeta {
  key: string
  count: number
}

export interface UseSessionSearchOptions {
  items: SessionMeta[]
  searchActive: boolean
  searchQuery: string
  workspaceId?: string
  currentFilter?: SessionFilter
  evaluateViews?: (meta: SessionMeta) => ViewConfig[]
  statusFilter?: Map<string, FilterMode>
  labelFilterMap?: Map<string, FilterMode>
  /** Collapsed group keys — collapsed items are excluded from pagination and flatItems */
  collapsedGroups?: Set<string>
  /** Grouping mode — needed to compute group keys for collapse-aware pagination */
  groupingMode?: 'date' | 'status'
  /** Ref to the ScrollArea viewport element — used for scroll-based pagination */
  scrollViewportRef?: React.RefObject<HTMLDivElement>
}

export interface UseSessionSearchResult {
  // Search state
  isSearchMode: boolean
  highlightQuery: string | undefined
  isSearchingContent: boolean
  /** Raw content search results — needed by SessionItem for `chatMatchCount` */
  contentSearchResults: Map<string, ContentSearchResult>

  // Filtered + grouped results
  matchingFilterItems: SessionMeta[]
  otherResultItems: SessionMeta[]
  exceededSearchLimit: boolean

  // Render-ready outputs
  flatItems: SessionMeta[]
  dateGroups: DateGroup[]
  sessionIndexMap: Map<string, number>

  // Pagination
  hasMore: boolean
  /** Metadata for collapsed groups (key + item count) — used to build header-only placeholder groups */
  collapsedGroupsMeta: CollapsedGroupMeta[]

  // Refs
  searchInputRef: React.RefObject<HTMLInputElement>
}

// ---------------------------------------------------------------------------
// Pure helpers (moved from SessionList)
// ---------------------------------------------------------------------------

function formatDateHeader(date: Date): string {
  if (isToday(date)) return "Today"
  if (isYesterday(date)) return "Yesterday"
  return format(date, "MMM d")
}

function groupSessionsByDate(sessions: SessionMeta[]): DateGroup[] {
  const groups = new Map<string, { date: Date; sessions: SessionMeta[] }>()

  for (const session of sessions) {
    const timestamp = session.lastMessageAt || 0
    const date = startOfDay(new Date(timestamp))
    const key = date.toISOString()

    if (!groups.has(key)) {
      groups.set(key, { date, sessions: [] })
    }
    groups.get(key)!.sessions.push(session)
  }

  return Array.from(groups.values())
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .map(group => ({
      ...group,
      label: formatDateHeader(group.date),
    }))
}

function getCollapseGroupKey(item: SessionMeta, groupingMode?: 'date' | 'status'): string {
  return groupingMode === 'status'
    ? `status-${getSessionStatus(item)}`
    : startOfDay(new Date(item.lastMessageAt || 0)).toISOString()
}

export interface CollapsedPaginationResult {
  paginatedItems: SessionMeta[]
  hasMore: boolean
  collapsedGroupsMeta: CollapsedGroupMeta[]
}

export function computeCollapsedPagination(
  items: SessionMeta[],
  displayLimit: number,
  collapsedGroups?: Set<string>,
  groupingMode?: 'date' | 'status',
): CollapsedPaginationResult {
  // Fast path: no collapse state → original slice
  if (!collapsedGroups || collapsedGroups.size === 0) {
    return {
      paginatedItems: items.slice(0, displayLimit),
      hasMore: displayLimit < items.length,
      collapsedGroupsMeta: [],
    }
  }

  const groupKeysInView = new Set(items.map(item => getCollapseGroupKey(item, groupingMode)))

  // Safety guard: don't allow collapse state to hide the entire list when only one
  // group exists in the current filtered view (there would be no meaningful collapse UX).
  if (groupKeysInView.size <= 1) {
    return {
      paginatedItems: items.slice(0, displayLimit),
      hasMore: displayLimit < items.length,
      collapsedGroupsMeta: [],
    }
  }

  const effectiveCollapsedKeys = new Set(
    Array.from(collapsedGroups).filter(key => groupKeysInView.has(key))
  )

  if (effectiveCollapsedKeys.size === 0) {
    return {
      paginatedItems: items.slice(0, displayLimit),
      hasMore: displayLimit < items.length,
      collapsedGroupsMeta: [],
    }
  }

  const expandedItems: SessionMeta[] = []
  const collapsedCounts = new Map<string, number>()

  for (const item of items) {
    const groupKey = getCollapseGroupKey(item, groupingMode)

    if (effectiveCollapsedKeys.has(groupKey)) {
      collapsedCounts.set(groupKey, (collapsedCounts.get(groupKey) || 0) + 1)
    } else {
      expandedItems.push(item)
    }
  }

  const meta: CollapsedGroupMeta[] = Array.from(collapsedCounts.entries()).map(
    ([key, count]) => ({ key, count })
  )

  return {
    paginatedItems: expandedItems.slice(0, displayLimit),
    hasMore: displayLimit < expandedItems.length,
    collapsedGroupsMeta: meta,
  }
}

/** Merge title + content matches and order by last activity, newest first. */
export function mergeSessionSearchResults(
  titleItems: SessionMeta[],
  contentOnlyItems: SessionMeta[],
): SessionMeta[] {
  return [...titleItems, ...contentOnlyItems].sort(
    (a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0)
  )
}

interface FilterMatchOptions {
  evaluateViews?: (meta: SessionMeta) => ViewConfig[]
  statusFilter?: Map<string, 'include' | 'exclude'>
  labelFilterMap?: Map<string, 'include' | 'exclude'>
}

export function sessionMatchesCurrentFilter(
  session: SessionMeta,
  currentFilter: SessionFilter | undefined,
  options: FilterMatchOptions = {}
): boolean {
  const { evaluateViews, statusFilter, labelFilterMap } = options

  const passesStatusFilter = (): boolean => {
    if (!statusFilter || statusFilter.size === 0) return true
    const sessionState = (session.sessionStatus || 'todo') as string

    let hasIncludes = false
    let matchesInclude = false
    for (const [stateId, mode] of statusFilter) {
      if (mode === 'exclude' && sessionState === stateId) return false
      if (mode === 'include') {
        hasIncludes = true
        if (sessionState === stateId) matchesInclude = true
      }
    }
    return !hasIncludes || matchesInclude
  }

  const passesLabelFilter = (): boolean => {
    if (!labelFilterMap || labelFilterMap.size === 0) return true
    const sessionLabelIds = session.labels?.map(l => parseLabelEntry(l).id) || []

    let hasIncludes = false
    let matchesInclude = false
    for (const [labelId, mode] of labelFilterMap) {
      if (mode === 'exclude' && sessionLabelIds.includes(labelId)) return false
      if (mode === 'include') {
        hasIncludes = true
        if (sessionLabelIds.includes(labelId)) matchesInclude = true
      }
    }
    return !hasIncludes || matchesInclude
  }

  if (!passesStatusFilter() || !passesLabelFilter()) return false

  if (!currentFilter) return true

  switch (currentFilter.kind) {
    case 'allSessions':
      return session.isArchived !== true

    case 'flagged':
      return session.isFlagged === true && session.isArchived !== true

    case 'archived':
      return session.isArchived === true

    case 'state':
      return (session.sessionStatus || 'todo') === currentFilter.stateId && session.isArchived !== true

    case 'label': {
      if (!session.labels?.length) return false
      if (session.isArchived === true) return false
      if (currentFilter.labelId === '__all__') return true
      const labelIds = session.labels.map(l => parseLabelEntry(l).id)
      return labelIds.includes(currentFilter.labelId)
    }

    case 'view':
      if (session.isArchived === true) return false
      if (!evaluateViews) return true
      const matched = evaluateViews(session)
      if (currentFilter.viewId === '__all__') return matched.length > 0
      return matched.some(v => v.id === currentFilter.viewId)

    default:
      const _exhaustive: never = currentFilter
      return true
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSessionSearch({
  items,
  searchActive,
  searchQuery,
  workspaceId,
  currentFilter,
  evaluateViews,
  statusFilter,
  labelFilterMap,
  collapsedGroups,
  groupingMode,
  scrollViewportRef,
}: UseSessionSearchOptions): UseSessionSearchResult {

  const [contentSearch, setContentSearch] = useState<{
    query: string
    results: Map<string, ContentSearchResult>
  }>({ query: '', results: EMPTY_CONTENT_RESULTS })
  const [isSearchingContent, setIsSearchingContent] = useState(false)
  const [displayLimit, setDisplayLimit] = useState(INITIAL_DISPLAY_LIMIT)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Search mode is active when search is open AND query has 2+ characters
  const isSearchMode = searchActive && searchQuery.length >= 2
  const highlightQuery = isSearchMode ? searchQuery : undefined

  // Ignore content hits from a previous query — the reset effect runs after paint,
  // so gating here prevents stale content-only rows from flashing while typing.
  const contentSearchResults = contentSearch.query === searchQuery
    ? contentSearch.results
    : EMPTY_CONTENT_RESULTS

  useEffect(() => {
    if (!isSearchMode) {
      setContentSearch({ query: '', results: EMPTY_CONTENT_RESULTS })
      setIsSearchingContent(false)
    }
  }, [isSearchMode])

  // --- Content search (ripgrep IPC — runs automatically in search mode) ---

  useEffect(() => {
    if (!workspaceId || !isSearchMode) {
      return
    }

    const searchId = Date.now().toString(36)
    searchLog.info('deep:query:start', { searchId, query: searchQuery })

    let cancelled = false
    setIsSearchingContent(true)

    const timer = setTimeout(async () => {
      try {
        searchLog.info('deep:ipc:call', { searchId })
        const ipcStart = performance.now()

        const results = await window.electronAPI.searchSessionContent(workspaceId, searchQuery, searchId)

        if (cancelled) return

        searchLog.info('deep:ipc:received', {
          searchId,
          durationMs: Math.round(performance.now() - ipcStart),
          resultCount: results.length,
        })

        const resultMap = new Map<string, ContentSearchResult>()
        for (const result of results) {
          resultMap.set(result.sessionId, {
            matchCount: result.matchCount,
            snippet: result.matches[0]?.snippet || '',
          })
        }
        setContentSearch({ query: searchQuery, results: resultMap })

        requestAnimationFrame(() => {
          searchLog.info('deep:render:complete', { searchId, sessionsDisplayed: resultMap.size })
        })
      } catch (error) {
        if (cancelled) return
        console.error('[useSessionSearch] Deep content search error:', error)
        setContentSearch({ query: searchQuery, results: EMPTY_CONTENT_RESULTS })
      } finally {
        if (!cancelled) {
          setIsSearchingContent(false)
        }
      }
    }, CONTENT_SEARCH_DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
      setIsSearchingContent(false)
    }
  }, [workspaceId, isSearchMode, searchQuery])

  // --- Focus search input when search activates ---

  useEffect(() => {
    if (searchActive) {
      searchInputRef.current?.focus()
    }
  }, [searchActive])

  // --- Data pipeline ---

  // Filter out hidden sessions before any processing
  const visibleItems = useMemo(() => items.filter(item => !item.hidden), [items])

  // Sort by most recent activity first
  const sortedItems = useMemo(() =>
    [...visibleItems].sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0)),
    [visibleItems]
  )

  // Apply current filter/view scope BEFORE any search — search always respects the active view
  const filterScopedItems = useMemo(() =>
    sortedItems.filter(item =>
      sessionMatchesCurrentFilter(item, currentFilter, { evaluateViews, statusFilter, labelFilterMap })
    ),
    [sortedItems, currentFilter, evaluateViews, statusFilter, labelFilterMap]
  )

  // Instant fuzzy title search over filter-scoped items
  const titleSearchResults = useMemo(() => {
    if (!isSearchMode) return []
    return fuzzyFilter(filterScopedItems, searchQuery, item => getSessionTitle(item))
  }, [filterScopedItems, isSearchMode, searchQuery])

  // Merge title matches with content matches, newest activity first
  const searchFilteredItems = useMemo(() => {
    if (!isSearchMode) return filterScopedItems

    const titleItems = titleSearchResults.map(r => r.item)
    const titleIds = new Set(titleItems.map(item => item.id))
    const contentOnlyItems = contentSearchResults.size === 0
      ? []
      : filterScopedItems.filter(item => !titleIds.has(item.id) && contentSearchResults.has(item.id))

    return mergeSessionSearchResults(titleItems, contentOnlyItems)
  }, [isSearchMode, filterScopedItems, titleSearchResults, contentSearchResults])

  // Split search results: matching current filter vs others
  const { matchingFilterItems, otherResultItems, exceededSearchLimit } = useMemo(() => {
    const hasActiveFilters =
      (currentFilter && currentFilter.kind !== 'allSessions') ||
      (statusFilter && statusFilter.size > 0) ||
      (labelFilterMap && labelFilterMap.size > 0)

    if (searchQuery.trim() && searchFilteredItems.length > 0) {
      searchLog.info('search:grouping', {
        searchQuery,
        currentFilterKind: currentFilter?.kind,
        currentFilterStateId: currentFilter?.kind === 'state' ? currentFilter.stateId : undefined,
        hasActiveFilters,
        statusFilterSize: statusFilter?.size ?? 0,
        labelFilterSize: labelFilterMap?.size ?? 0,
        itemCount: searchFilteredItems.length,
      })
    }

    const totalCount = searchFilteredItems.length
    const exceeded = totalCount > MAX_SEARCH_RESULTS

    if (!isSearchMode || !hasActiveFilters) {
      const limitedItems = searchFilteredItems.slice(0, MAX_SEARCH_RESULTS)
      return { matchingFilterItems: limitedItems, otherResultItems: [] as SessionMeta[], exceededSearchLimit: exceeded }
    }

    const matching: SessionMeta[] = []
    const others: SessionMeta[] = []

    for (const item of searchFilteredItems) {
      if (matching.length + others.length >= MAX_SEARCH_RESULTS) break

      const matches = sessionMatchesCurrentFilter(item, currentFilter, { evaluateViews, statusFilter, labelFilterMap })
      if (matches) {
        matching.push(item)
      } else {
        others.push(item)
      }
    }

    if (searchFilteredItems.length > 0) {
      searchLog.info('search:grouping:result', {
        matchingCount: matching.length,
        othersCount: others.length,
        exceeded,
      })
    }

    return { matchingFilterItems: matching, otherResultItems: others, exceededSearchLimit: exceeded }
  }, [searchFilteredItems, currentFilter, evaluateViews, isSearchMode, statusFilter, labelFilterMap, searchQuery])

  // --- Pagination ---

  useEffect(() => {
    setDisplayLimit(INITIAL_DISPLAY_LIMIT)
  }, [searchQuery])

  // Collapse-aware pagination: collapsed items are excluded entirely from
  // paginatedItems (and therefore flatItems / keyboard nav). Their counts are
  // returned as collapsedGroupsMeta so the renderer can show header-only groups.
  const { paginatedItems, hasMore, collapsedGroupsMeta } = useMemo(() => {
    return computeCollapsedPagination(searchFilteredItems, displayLimit, collapsedGroups, groupingMode)
  }, [searchFilteredItems, displayLimit, collapsedGroups, groupingMode])

  const loadMore = useCallback(() => {
    setDisplayLimit(prev => Math.min(prev + BATCH_SIZE, searchFilteredItems.length))
  }, [searchFilteredItems.length])

  // Scroll-based pagination: listen for scroll on the actual ScrollArea viewport
  // (IntersectionObserver with root=null doesn't detect scroll inside Radix ScrollArea)
  useEffect(() => {
    if (!hasMore) return
    const viewport = scrollViewportRef?.current
    if (!viewport) return

    const check = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport
      if (scrollHeight - scrollTop - clientHeight < 200) {
        loadMore()
      }
    }

    check() // fill viewport on mount / after group expand
    viewport.addEventListener('scroll', check, { passive: true })
    return () => viewport.removeEventListener('scroll', check)
  }, [hasMore, loadMore, displayLimit, scrollViewportRef])

  // --- Derived render data ---

  const dateGroups = useMemo(() => groupSessionsByDate(paginatedItems), [paginatedItems])

  const flatItems = useMemo(() => {
    if (isSearchMode) {
      return [...matchingFilterItems, ...otherResultItems]
    }
    return dateGroups.flatMap(group => group.sessions)
  }, [isSearchMode, matchingFilterItems, otherResultItems, dateGroups])

  const sessionIndexMap = useMemo(() => {
    const map = new Map<string, number>()
    flatItems.forEach((item, index) => map.set(item.id, index))
    return map
  }, [flatItems])

  return {
    isSearchMode,
    highlightQuery,
    isSearchingContent,
    contentSearchResults,
    matchingFilterItems,
    otherResultItems,
    exceededSearchLimit,
    flatItems,
    dateGroups,
    sessionIndexMap,
    hasMore,
    collapsedGroupsMeta,
    searchInputRef,
  }
}
